import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import matter from 'gray-matter';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Buffer } from 'buffer';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

if (!window.Buffer) window.Buffer = Buffer;

const guides = import.meta.glob('../content/guides/*.md', {
    query: '?raw',
    import: 'default',
});

interface Frontmatter {
    title: string;
    category: string;
    mode: string;
    image?: string;
    tags?: string[];
    published?: boolean;
}

export default function GuideViewer({ darkMode }: { darkMode: boolean }) {
    const { slug } = useParams();
    const [content, setContent] = useState('');
    const [frontmatter, setFrontmatter] = useState<Frontmatter | null>(null);

    useEffect(() => {
        const filePath = `../content/guides/${slug}.md`;
        const load = async () => {
            const loader = guides[filePath];
            if (loader) {
                const raw = (await loader()) as string;
                const { content, data } = matter(raw);
                setContent(content);
                setFrontmatter(data as Frontmatter);
            } else {
                setContent('Guide not found.');
            }
        };
        load();
    }, [slug]);

    return (
        <div
            className={`min-h-screen w-full h-full ${
                darkMode
                    ? 'bg-gray-900 text-white'
                    : 'bg-gradient-to-br from-indigo-300 via-purple-300 to-pink-200 text-gray-800'
            } flex items-center justify-center p-4`}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className='w-full max-w-4xl'
            >
                <Card className={`border-0 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white/80 text-gray-900'} shadow-xl rounded-2xl backdrop-blur p-6 md:p-10`}>
                    <CardHeader>
                        <CardTitle className='text-center text-3xl md:text-4xl font-extrabold mb-4'>
                            {frontmatter?.title}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {frontmatter?.image && (
                            <img
                                src={frontmatter.image}
                                alt={frontmatter.title}
                                className='mb-6 rounded-xl shadow-lg w-full object-cover'
                            />
                        )}
                        <div className='flex gap-4 text-sm mb-6 justify-center'>
                            <span>🗂 {frontmatter?.category}</span>
                            <span>🎮 {frontmatter?.mode}</span>
                        </div>
                        <div
                            className={`prose max-w-none
                prose-headings:font-bold
                prose-h1:text-4xl prose-h1:mb-4
                prose-h2:text-2xl prose-h2:border-b prose-h2:border-gray-500 prose-h2:pb-1 prose-h2:mb-6
                prose-h3:text-xl prose-h3:mt-6
                prose-p:leading-relaxed
                prose-ul:pl-6 prose-ol:pl-6
                prose-li:marker:text-purple-500
                ${darkMode ? 'text-white prose-h1:text-white prose-h2:text-white prose-h3:text-white prose-strong:text-purple-300 prose-a:text-blue-400 prose-blockquote:text-gray-200 prose-blockquote:bg-gray-700' : 'prose-strong:text-purple-700 prose-a:text-blue-600 prose-blockquote:text-gray-600 prose-blockquote:bg-purple-100'}
                prose-blockquote:pl-4 prose-blockquote:pr-4 prose-blockquote:py-2 prose-blockquote:rounded-lg prose-blockquote:border-l-4 prose-blockquote:border-purple-500`}
                        >
                            <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                                {content}
                            </ReactMarkdown>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}
